import React, { useEffect, useState } from "react";
import { Button } from "../../../components/catalyst/button";
import { Badge } from "../../../components/catalyst/badge";
import {
  ChevronLeftIcon,
  ClockIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { auth, db, functions } from "../../../config/firebaseConfig";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import MemberDetailsCard from "../../../components/MemberDetailsCard";
import { getStatusBadgeClass } from "../../../util/statusBadge";
import { httpsCallable } from "firebase/functions";
import { useToast } from "../../../util/ToastProvider";
import { getAuth } from "firebase/auth";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { idText } from "typescript";

export default function ViewReport() {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<any | null>(null);
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");        // "member-list"
  const memberId = searchParams.get("memberId");
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRecommendationDialog, setShowRecommendationDialog] = useState(false);
  const [recommendationsArray, setRecommendationsArray] = useState<any[]>([])
  const [loadingRecommendations,setLoadingRecommendations] = useState(false)
  const toaster = useToast();
  const { user } = useSelector((s: RootState) => s.auth);

  const formatDate = (ts: Timestamp | null | undefined) => {
    if (!ts) return "Unknown date";
    const date = ts.toDate();
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!id) return;
    callReport()
    const unsub = onSnapshot(doc(db, "reports", id), async (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();

      const annexureRef = collection(db, "reports", id, "annexureResults");
      const annexureSnap = await getDocs(annexureRef);
      const annexureResults = annexureSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));


      setReport({ ...data, annexureResults });
      console.log("dtata", data)
      console.log("dtata")
      setLoading(false);
    });

    return () => unsub();
  }, [id]);

  const handleEditReport = async () => {
    if (!id) return;
    try {
      setIsProcessing(true);
      const performReportReviewTask = httpsCallable(functions, "performReportReview");
      const res: any = await performReportReviewTask({
        action: "toggleDraftToReview",
        reportId: id,
      });

      toaster.success(res.data?.message || "Status toggled successfully");
      const status = res.data?.status;
      if (status === "IN_REVIEW") {
        nav(`/admin/report/update/${id}`);
      }
    } catch (err: any) {
      console.error("Toggle error:", err);
      toaster.error(err.message || "Failed to toggle review mode.");
    } finally {
      setIsProcessing(false);
    }
  };

  // 🔘 Confirm & Approve
  const handleApprove = async () => {
    if (!id) return;
    try {
      if (!auth.currentUser) throw new Error("User not signed in");
      setIsProcessing(true);
      const performReportReviewTask = httpsCallable(functions, "performReportReview");
      const res: any = await performReportReviewTask({
        action: "approve",
        reportId: id,
      });
      toaster.success(res.data?.message || "Report approved successfully");
      setShowConfirmDialog(false);
      nav("/admin")
    } catch (err: any) {
      console.error("Approve error:", err);
      toaster.error(err.message || "Failed to approve report.");
    } finally {
      setIsProcessing(false);
    }
  };

  const loadRecommendations = async () => {
    setLoadingRecommendations(true);
  
    try {
      if (!report?.user?.userId) {
        toaster.error("Invalid user ID in report");
        return;
      }
  
      // 1️⃣ Fetch user document
      const ref = doc(db, "users", report.user.userId);
      const snap = await getDoc(ref);
  
      if (!snap.exists()) {
        toaster.error("Error fetching recommendations");
        return;
      }
  
      const userData = snap.data();
      const savedRecs = Array.isArray(userData.recommendations)
        ? userData.recommendations
        : [];
  
      // Mark ALL saved recs as selected = true
      const savedRecsNormalized = savedRecs.map((r: any) => ({
        ...r,
        selected: true,   // auto-select from Firestore
        purchased: !!r.purchased,
      }));
  
      // 2️⃣ Fetch backend templates
      const fn = httpsCallable(functions, "templateManager");
      const resp: any = await fn({
        action: "nextUnlockableReports",
        uid: report.user.userId,
      });
  
      const backendTemplates = (resp?.data?.templates || []).map((tpl: any) => ({
        ...tpl,
        selected: false,           // default: unchecked
        purchased: !!tpl.purchased // backend sets this
      }));
  
      // 3️⃣ Merge + dedupe (Firestore recs override backend)
      const combined = [...backendTemplates, ...savedRecsNormalized];
  
      const deduped = combined.reduce((acc: any[], cur: any) => {
        const existing = acc.find((i) => i.id === cur.id);
  
        if (!existing) {
          acc.push(cur);
        } else {
          // Firestore recs override
          existing.selected = existing.selected || cur.selected;
          existing.purchased = existing.purchased || cur.purchased;
        }
  
        return acc;
      }, []);
  
      // 4️⃣ Update UI
      setRecommendationsArray(deduped);
      setShowRecommendationDialog(true);
  
    } catch (err: any) {
      console.error(err);
      toaster.error("Failed to load recommendations.");
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const goBackToList = () => {
    const params = new URLSearchParams();
  
    // read common params
    const q = searchParams.get("q");
    const f = searchParams.get("f");
  
    if (q) params.set("q", q);
    if (f) params.set("f", f);
  
    // MEMBER LIST (supports q, f, s, p)
    if (from === "member-list") {
      const s = searchParams.get("s");
      const p = searchParams.get("p");
  
      if (s) params.set("s", s);
      if (p) params.set("p", p);
  
      const query = params.toString();
      nav(query ? `/admin/members?${query}` : "/admin/members");
      return;
    }
  
    // ADMIN LIST (supports only q, f)
    if (from === "admin-list") {
      const query = params.toString();
      nav(query ? `/admin?${query}` : "/admin");
      return;
    }
  
    // MEMBER DETAIL
    if (from === "member-view" && memberId) {
      nav(`/admin/members/view/${memberId}`);
      return;
    }
  
    // DEFAULT FALLBACK
    nav("/admin");
  };
  
  
  
  
  
  
  const handleRecommendations = async () => {
    if (!report?.user?.userId) {
      toaster.error("Invalid user ID");
      return;
    }
  
    try {
      setIsProcessing(true);
  
      // 1️⃣ Extract selected items only
      const selected = recommendationsArray
        .filter((r: any) => r.selected && !r.purchased) // do NOT save purchased items
        .map((r: any) => ({
          id: r.id,
          title: r.title,
          shortDesc: r.shortDesc ?? "",
          url: r.url ?? "",
        }));
  
      // 2️⃣ Save into Firestore
      await updateDoc(doc(db, "users", report.user.userId), {
        recommendations: selected,
        updatedAt: Timestamp.now(),
      });
  
      // 3️⃣ UI feedback
      toaster.success("Recommendations updated successfully.");
      setShowRecommendationDialog(false);
  
    } catch (err: any) {
      console.error(err);
      toaster.error("Failed to save recommendations.");
    } finally {
      setIsProcessing(false);
    }
  };
  

  const callReport = async () => {
    const performReportReviewTask = httpsCallable(functions, "performReportReview");
    const res: any = await performReportReviewTask({
      action: "refreshLock",
      reportId: id,
    });
  }


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        Loading report...
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-600">
        <p>No report found.</p>
        <Button className="mt-3" onClick={() => nav("/admin")}>
          Go back
        </Button>
      </div>
    );
  }



  const latestKey =
    report.latestReviewVersion > 1
      ? String(report.latestReviewVersion)
      : "1";
  const activeData =
    (report.reviewedData && report.reviewedData[latestKey]) ||
    report.generatedData ||
    {};
  const markdownContent =
    activeData.text || activeData.data || "_No content available._";
  const version = report.latestReviewVersion || "1";
  console.log(activeData)
  const versionDate = activeData.createdAt ?
    formatDate(activeData.createdAt) :
    formatDate(activeData.generatedAt);
  const title = report.template?.templateTitle || "Untitled Report";
  const member = report.user || {};
  const status = report.status || "DRAFT";

  const currentUser = getAuth().currentUser;
  const currentUserId = currentUser?.uid;
  const isReviewer =
    report?.review?.reviewerId && report.review.reviewerId === currentUserId;
  const statusLower = status.toUpperCase();

  return (
    <div className="mx-8 my-6 relative">
      {/* Back Button */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => goBackToList()}
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Reports
        </button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <h1 className="mb-4 text-[24px] font-semibold text-[rgba(106,114,130,1)]">
          View report
        </h1>

        <div className="flex gap-3">
          {(statusLower === "DRAFT" || statusLower === 'FAILED') && (
            <Button
              color="soft"
              className="text-sm"
              onClick={handleEditReport}
              disabled={isProcessing}
            >
              {isProcessing ? "Please wait..." : "Edit"}
            </Button>
          )}

          {statusLower === "IN_REVIEW" && isReviewer && (
            <Button
              color="soft"
              className="text-sm"
              onClick={() => nav(`/admin/report/update/${id}`)}
              disabled={isProcessing}
            >
              Go to Review
            </Button>
          )}

          {/* {statusLower !== "IN_REVIEW" && (
            <Button color="soft" className="text-sm" disabled={isProcessing}>
              PDF Preview
            </Button>
          )} */}

        {(user?.role === "admin" || user?.role === "approver") && (
          <>
          {statusLower === "DRAFT" && (
            <Button
              color="brand"
              className="text-white text-sm"
              onClick={() => setShowConfirmDialog(true)}
              disabled={isProcessing}
            >
              <CheckCircleIcon className="h-4 w-4" />
              Approve
            </Button>
          )}
          </>
          )}
          {statusLower === "SENT" && (
  <Button
    color="soft"
    className={`text-sm flex items-center gap-2 ${
      loadingRecommendations ? "opacity-60 cursor-not-allowed" : ""
    }`}
    onClick={() => {
      if (!loadingRecommendations) loadRecommendations();
    }}
    disabled={isProcessing || loadingRecommendations}
  >
    {loadingRecommendations && (
      <svg
        className="animate-spin h-4 w-4 text-gray-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    )}

    {loadingRecommendations ? "Fetching Recommendations…" : "Update Recommendations"}
  </Button>
)}

          
        </div>

      </div>

      {/* Title + Status */}
      <h1 className="mb-4 text-[24px] font-medium text-black flex items-center gap-3">
        {title}
        {report.status && (
          <Badge
            className={`${getStatusBadgeClass(
              report.status
            )} capitalize bold text-xs px-2 py-0.5`}
          >
            {report.status}
          </Badge>
        )}
      </h1>

      {/* Version Info */}
      <div className="w-fit whitespace-normal p-1 rounded-lg flex items-center bg-[rgba(249,250,251,1)] border border-[rgba(229,231,235,1)] text-[14px] font-medium text-black gap-2 text-gray-600 mb-6">
        <ClockIcon className="w-4 h-4 text-gray-500" />
        <span>
          version {version}, {versionDate}
        </span>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-6">
        <div className="prose max-w-none text-sm text-gray-800 leading-relaxed">
        {report.status !== 'failed' && (<ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
          >
            {markdownContent}
          </ReactMarkdown>)}

          {report.status === 'failed' && (
            <div className="bg-dark-bg-danger-medium p-6">
              <div className="mb-4 text-base">This report could not be processed due to the following error.</div>
              <div className="text-lg mb-4 text-dark-text-danger-strong">{report.errorMessage}</div>
              <div className="text-sm text-dark-border">You can either re-run the report, or create a blank draft report that you can manually edit.</div>
            </div>
          )}


        {/* Annexures */}
        {report.annexureResults && report.annexureResults.length > 0 && (
          <div className="mt-10 grid grid-cols-1  gap-6">
            <div className="space-y-4">
            <h2 className="text-lg font-semibold text-zinc-800 mb-4">
                          Annexure Reports
            </h2>
              {report.annexureResults.map((annexure: any) => (
                <div
                  key={annexure.id}
                  className="border border-gray-200 rounded-lg p-4 pt-0 bg-gray-50 shadow-sm"
                >
                  <h3 className="text-md font-semibold text-gray-800">
                    {annexure.title || `Annexure ${annexure.id}`}
                  </h3>
                  {annexure.output && (
                    <div className="prose text-sm mt-2">
                      <ReactMarkdown>{annexure.output}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div>
            </div>
          </div>

        )}
        </div> 


        {member && (
          <div className="self-start">
            <MemberDetailsCard data={report.user} showMath={true} />
          </div>
        )}
      </div>

      

      {/* 🟣 Approve Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-[500px]">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Approve Report
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to approve this report? Once approved, it
              will be marked as <b>Final</b> and can’t be edited.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                color="soft"
                onClick={() => setShowConfirmDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                color="brand"
                className="text-white"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? "Approving..." : "Yes, Approve"}
              </Button>
            </div>
          </div>
        </div>
      )}


       {/* 🟣 Recommendation  Dialog */}
       {showRecommendationDialog && (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
    <div className="bg-white rounded-lg shadow-xl p-6 w-[500px] max-h-[80vh] overflow-y-auto">
      
      <h2 className="text-lg font-semibold text-gray-800 mb-1">
        Recommended reports
      </h2>
      <p className="text-sm text-gray-600 mb-5">
        These reports appear in the member’s landing page. Reports already requested by the member cannot be selected.
      </p>

      <div className="space-y-2">
  {recommendationsArray.map((rec: any) => {
    const isPurchased = rec.purchased === true;

    return (
      <label
        key={rec.id}
        className={`
          flex items-center gap-3 px-3 py-2 rounded 
          ${!isPurchased ? "hover:bg-gray-100 cursor-pointer" : "opacity-50 cursor-not-allowed"}
        `}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={rec.selected ?? false}
          disabled={isPurchased}
          onChange={(e) => {
            if (isPurchased) return;
            setRecommendationsArray((old) =>
              old.map((r) =>
                r.id === rec.id ? { ...r, selected: e.target.checked } : r
              )
            );
          }}
          className="h-4 w-4 disabled:cursor-not-allowed"
        />

        {/* Report Title */}
        <span
          className={`text-sm ${
            isPurchased ? "text-gray-500" : "text-gray-800"
          }`}
        >
          {rec.title}
        </span>

        {/* Purchased tag */}
        {isPurchased && (
          <span className="ml-auto text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
            Purchased
          </span>
        )}
      </label>
    );
  })}
</div>


      <div className="flex justify-end gap-3 mt-6">
        <Button
          color="soft"
          onClick={() => setShowRecommendationDialog(false)}
          disabled={isProcessing}
        >
          Cancel
        </Button>

        <Button
          color="brand"
          className="text-white"
          onClick={handleRecommendations}
          disabled={isProcessing}
        >
          {isProcessing ? "Saving..." : "Update"}
        </Button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}
